import type { SharedBillingPlan } from "../../../../../../shared/frontend/billing/sharedBillingPageTypes";
import { formatNumber } from "../units/format";

type Translate = (key: string) => string;

/**
 * Mengenlimits, die eine Tarifversion setzen kann. Ist ein Schlüssel gesetzt, wird er im Tarif
 * angezeigt und beim Anlegen geprüft (`TenantLimitService`); fehlt er, ist die Menge unbegrenzt.
 */
export const M1MEMBER_QUANTITY_LIMITS = [
  { key: "maxMembers", label: "Members", upTo: "up to {count} members" },
  { key: "maxUsers", label: "Users", upTo: "up to {count} users" },
  { key: "maxReaders", label: "Reader devices", upTo: "up to {count} reader devices" },
] as const;

const AI_MONTHLY_LIMIT_KEY = "aiInboundMonthlyLimit";

const withCount = (template: string, count: number) => template.replace("{count}", formatNumber(count));

function limitOf(plan: SharedBillingPlan, key: string): number | null {
  const value = plan.limits?.[key];
  return typeof value === "number" ? value : null;
}

/** Standorte aus Mindest-/Höchstanzahl des Tarifs — die Standortgrenze ist kein eigenes Limit. */
export function describePlanLocations(plan: SharedBillingPlan, tr: Translate): { text: string; hint: string } {
  if (plan.priceOnRequest) return { text: tr("any number"), hint: tr("individual prices") };
  if (plan.maxQuantity === 1) return { text: tr("1 location"), hint: tr("single license") };
  if (plan.maxQuantity != null) return { text: withCount(tr("up to {count} locations"), plan.maxQuantity), hint: tr("bookable") };
  return { text: tr("any number"), hint: tr("bookable") };
}

export function describePlanAiQuota(plan: SharedBillingPlan, tr: Translate): string | null {
  const limit = limitOf(plan, AI_MONTHLY_LIMIT_KEY);
  return limit == null ? null : withCount(tr("{count} / mo."), limit);
}

/** Mengenlimit als Text; `null`, wenn der Tarif es nicht setzt (unbegrenzt). */
export function describePlanQuantityLimit(plan: SharedBillingPlan, key: string, template: string, tr: Translate): string | null {
  const limit = limitOf(plan, key);
  return limit == null ? null : withCount(tr(template), limit);
}

/** Zeilen für die Tarifkarte: Standorte, KI-Kontingent und jedes gesetzte Mengenlimit. */
export function planLimitDetails(plan: SharedBillingPlan, tr: Translate): string[] {
  // Bei Preis auf Anfrage werden Mengen und Leistungsumfang individuell vereinbart.
  if (plan.priceOnRequest) return [];
  const locations = describePlanLocations(plan, tr);
  const lines = [
    plan.maxQuantity === 1 ? `${locations.text} (${locations.hint})`
      : plan.maxQuantity != null ? locations.text
        : tr("any number of locations bookable"),
  ];
  for (const limit of M1MEMBER_QUANTITY_LIMITS) {
    const text = describePlanQuantityLimit(plan, limit.key, limit.upTo, tr);
    if (text) lines.push(text);
  }
  const ai = limitOf(plan, AI_MONTHLY_LIMIT_KEY);
  if (ai != null) lines.push(withCount(tr("{count} AI contract cases / month included"), ai));
  return lines;
}
